/**
 * Client-side Error Tracking Utility
 * Captures and reports frontend errors to the server
 */

class ErrorTracker {
  constructor() {
    this.errorCount = 0;
    this.recentErrors = [];
    this.isInitialized = false;
    this.maxStoredErrors = 10;
    this.errorReportingEndpoint = '/api/monitoring/client-error';
    
    // Error reporting throttling
    this.lastReportTime = 0;
    this.reportingThrottleMs = 5000; // Don't report more than once every 5 seconds
    
    // Configuration options
    this.options = {
      captureUnhandledErrors: true,
      capturePromiseRejections: true,
      captureNetworkErrors: true,
      captureConsoleErrors: true,
      reportErrors: true,
      recordMetadata: true,
      includeStackTrace: true,
    };
  }
  
  /**
   * Initialize error tracking
   * @param {Object} options - Configuration options
   */
  init(options = {}) {
    if (this.isInitialized) return;
    
    // Merge options
    this.options = { ...this.options, ...options };
    
    // Setup error event listeners
    if (this.options.captureUnhandledErrors) {
      window.addEventListener('error', this.handleUnhandledError.bind(this));
    }
    
    if (this.options.capturePromiseRejections) {
      window.addEventListener('unhandledrejection', this.handlePromiseRejection.bind(this));
    }
    
    if (this.options.captureConsoleErrors) {
      this.interceptConsoleErrors();
    }
    
    // Capture network errors if enabled
    if (this.options.captureNetworkErrors) {
      this.interceptFetchErrors();
      this.interceptXHRErrors();
    }
    
    this.isInitialized = true;
    
    // Log initialization
    console.log('Error tracker initialized');
  }
  
  /**
   * Handle unhandled errors (from window.onerror)
   * @param {ErrorEvent} event - Error event
   */
  handleUnhandledError(event) {
    const { message, filename, lineno, colno, error } = event;
    
    // Ignore errors from extensions or third-party scripts
    if (filename && !this.isSameOrigin(filename)) {
      return;
    }
    
    const errorInfo = {
      type: 'unhandled_error',
      message,
      source: filename,
      line: lineno,
      column: colno,
      timestamp: new Date().toISOString(),
      stack: error?.stack,
      isBrowserExtension: this.isExtensionError(error),
    };
    
    this.processError(errorInfo);
  }
  
  /**
   * Handle unhandled promise rejections
   * @param {PromiseRejectionEvent} event - Promise rejection event
   */
  handlePromiseRejection(event) {
    const reason = event.reason;
    let errorInfo = {
      type: 'unhandled_rejection',
      timestamp: new Date().toISOString(),
    };
    
    if (reason instanceof Error) {
      errorInfo = {
        ...errorInfo,
        message: reason.message,
        stack: reason.stack,
        name: reason.name,
      };
    } else {
      errorInfo.message = String(reason);
    }
    
    this.processError(errorInfo);
  }
  
  /**
   * Intercept console.error calls
   */
  interceptConsoleErrors() {
    const originalConsoleError = console.error;
    
    console.error = (...args) => {
      // Call original console.error
      originalConsoleError.apply(console, args);
      
      // Process the error
      let errorInfo = {
        type: 'console_error',
        message: args.map(arg => this.stringifyArg(arg)).join(' '),
        timestamp: new Date().toISOString(),
      };
      
      // Check if first argument is an error
      if (args[0] instanceof Error) {
        errorInfo.stack = args[0].stack;
        errorInfo.name = args[0].name;
      }
      
      this.processError(errorInfo);
    };
  }
  
  /**
   * Intercept fetch errors
   */
  interceptFetchErrors() {
    const originalFetch = window.fetch;

    window.fetch = async (resource, options) => {
      try {
        const response = await originalFetch(resource, options);

        // Only capture server errors (5xx) and not client errors (4xx)
        // 4xx errors are often expected (like 404 when equipment not found)
        if (!response.ok && response.status >= 500) {
          this.captureNetworkError({
            type: 'fetch_error',
            url: typeof resource === 'string' ? resource : resource.url,
            status: response.status,
            statusText: response.statusText,
            method: options?.method || 'GET',
          });
        }

        return response;
      } catch (error) {
        // Only capture actual network errors, not application errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          this.captureNetworkError({
            type: 'fetch_network_error',
            url: typeof resource === 'string' ? resource : resource.url,
            method: options?.method || 'GET',
            error: error.message,
            stack: error.stack,
          });
        }

        throw error;
      }
    };
  }
  
  /**
   * Intercept XMLHttpRequest errors
   */
  interceptXHRErrors() {
    // In a development environment, we'll use a simplified version to avoid issues
    if (process.env.NODE_ENV === 'development') {
      console.log('XHR error tracking minimized in development mode');
      return;
    }
    
    try {
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;
      const self = this;
      
      XMLHttpRequest.prototype.open = function(method, url) {
        this._errorTracker = {
          method,
          url,
        };
        originalOpen.apply(this, arguments);
      };
      
      XMLHttpRequest.prototype.send = function() {
        if (!this._errorTracker) {
          this._errorTracker = {}; // Fallback if open wasn't called
        }
        
        const xhr = this;
        
        // Add load and error handlers
        xhr.addEventListener('load', function() {
          // Only capture server errors (5xx), not client errors (4xx)
          if (xhr.status >= 500) {
            self.captureNetworkError({
              type: 'xhr_error',
              url: xhr._errorTracker.url,
              status: xhr.status,
              statusText: xhr.statusText,
              method: xhr._errorTracker.method || 'unknown',
            });
          }
        });
        
        xhr.addEventListener('error', function() {
          self.captureNetworkError({
            type: 'xhr_error',
            url: xhr._errorTracker.url,
            method: xhr._errorTracker.method || 'unknown',
            error: 'Network error',
          });
        });
        
        // Continue with the original send
        originalSend.apply(this, arguments);
      };
    } catch (err) {
      console.error('Failed to set up XHR error tracking:', err);
    }
  }
  
  /**
   * Process and store error information
   * @param {Object} errorInfo - Error information
   */
  processError(errorInfo) {
    // Increment error count
    this.errorCount++;
    
    // Add metadata if enabled
    if (this.options.recordMetadata) {
      errorInfo.metadata = this.collectMetadata();
    }
    
    // Store in recent errors
    this.storeError(errorInfo);
    
    // Report to server if enabled
    if (this.options.reportErrors) {
      this.reportError(errorInfo);
    }
  }
  
  /**
   * Capture a network error
   * @param {Object} errorInfo - Network error information
   */
  captureNetworkError(errorInfo) {
    // Skip reporting for non-application endpoints
    if (errorInfo.url && !this.isAppEndpoint(errorInfo.url)) {
      return;
    }
    
    // Process the error
    this.processError({
      ...errorInfo,
      timestamp: new Date().toISOString(),
    });
  }
  
  /**
   * Store error information for later access
   * @param {Object} errorInfo - Error information
   */
  storeError(errorInfo) {
    this.recentErrors.unshift(errorInfo);
    
    // Limit the size of stored errors
    if (this.recentErrors.length > this.maxStoredErrors) {
      this.recentErrors = this.recentErrors.slice(0, this.maxStoredErrors);
    }
    
    // Store in session storage for persistence across page loads
    try {
      const storedErrors = JSON.parse(sessionStorage.getItem('recentErrors') || '[]');
      storedErrors.unshift(errorInfo);
      
      // Limit stored errors
      const limitedErrors = storedErrors.slice(0, this.maxStoredErrors);
      sessionStorage.setItem('recentErrors', JSON.stringify(limitedErrors));
    } catch (e) {
      // Ignore storage errors
    }
  }
  
  /**
   * Report error to the server
   * @param {Object} errorInfo - Error information
   */
  reportError(errorInfo) {
    // Implement throttling to prevent flooding the server
    const now = Date.now();
    if (now - this.lastReportTime < this.reportingThrottleMs) {
      return;
    }
    
    this.lastReportTime = now;
    
    // Skip reporting if in development mode with specific patterns
    if (process.env.NODE_ENV === 'development') {
      if (errorInfo.message && (
        errorInfo.message.includes('ResizeObserver loop') ||
        errorInfo.message.includes('Loading chunk') ||
        errorInfo.message.includes('NetworkError') ||
        errorInfo.message.includes('Failed to fetch') ||
        errorInfo.message.includes('404') ||
        errorInfo.message.includes('Not Found') ||
        errorInfo.message.includes('No equipment found') ||
        errorInfo.status === 404 ||
        errorInfo.status === 400
      )) {
        return;
      }
    }

    // Skip reporting expected API errors regardless of environment
    if (errorInfo.status && (
      errorInfo.status === 404 || // Not found - expected in many cases
      errorInfo.status === 400    // Bad request - usually validation errors
    )) {
      return;
    }

    // Skip reporting common "not found" messages
    if (errorInfo.message && (
      errorInfo.message.includes('No equipment found') ||
      errorInfo.message.includes('Equipment not found') ||
      errorInfo.message.includes('Not found')
    )) {
      return;
    }
    
    // Send error to server
    try {
      fetch(this.errorReportingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorInfo),
        // Don't report errors from this request (would cause infinite loop)
        keepalive: true,
      }).catch(() => {
        // Ignore errors from the reporting itself
      });
    } catch (e) {
      // Ignore errors from the reporting itself
    }
  }
  
  /**
   * Manually capture and report an error
   * @param {Error|string} error - Error object or message
   * @param {Object} additionalInfo - Additional context for the error
   */
  captureError(error, additionalInfo = {}) {
    let errorInfo = {
      type: 'manual',
      timestamp: new Date().toISOString(),
      ...additionalInfo,
    };
    
    if (error instanceof Error) {
      errorInfo = {
        ...errorInfo,
        message: error.message,
        name: error.name,
        stack: this.options.includeStackTrace ? error.stack : undefined,
      };
    } else {
      errorInfo.message = String(error);
    }
    
    this.processError(errorInfo);
  }
  
  /**
   * Get all recent errors
   * @returns {Array} Recent errors
   */
  getRecentErrors() {
    try {
      // Merge stored errors with in-memory errors
      const storedErrors = JSON.parse(sessionStorage.getItem('recentErrors') || '[]');
      const mergedErrors = [...this.recentErrors];
      
      // Add stored errors that aren't already in memory
      storedErrors.forEach(storedError => {
        if (!mergedErrors.some(e => 
          e.timestamp === storedError.timestamp && 
          e.message === storedError.message)) {
          mergedErrors.push(storedError);
        }
      });
      
      // Sort by timestamp (newest first)
      return mergedErrors.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      ).slice(0, this.maxStoredErrors);
    } catch (e) {
      return this.recentErrors;
    }
  }
  
  /**
   * Clear all stored errors
   */
  clearErrors() {
    this.recentErrors = [];
    this.errorCount = 0;
    
    try {
      sessionStorage.removeItem('recentErrors');
    } catch (e) {
      // Ignore storage errors
    }
  }
  
  /**
   * Collect metadata about the environment
   * @returns {Object} Environment metadata
   * @private
   */
  collectMetadata() {
    const metadata = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      viewportSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      referrer: document.referrer,
      // Add app-specific info if available
      userId: window.currentUser?.id,
      username: window.currentUser?.email,
    };
    
    // Safely add time on page if performance API is available
    try {
      if (window.performance && window.performance.timing && window.performance.timing.navigationStart) {
        metadata.timeOnPage = Math.floor((Date.now() - window.performance.timing.navigationStart) / 1000);
      } else if (window.performance && window.performance.timeOrigin) {
        metadata.timeOnPage = Math.floor((Date.now() - window.performance.timeOrigin) / 1000);
      }
    } catch (e) {
      // Ignore performance API errors
    }
    
    return metadata;
  }
  
  /**
   * Check if a URL is from the same origin
   * @param {string} url - URL to check
   * @returns {boolean} Whether URL is from same origin
   * @private
   */
  isSameOrigin(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Check if an error is likely from a browser extension
   * @param {Error} error - Error to check
   * @returns {boolean} Whether error is likely from extension
   * @private
   */
  isExtensionError(error) {
    if (!error || !error.stack) return false;
    
    return error.stack.includes('chrome-extension://') || 
           error.stack.includes('moz-extension://') ||
           error.stack.includes('safari-extension://');
  }
  
  /**
   * Check if a URL is an application endpoint
   * @param {string} url - URL to check
   * @returns {boolean} Whether URL is an app endpoint
   * @private
   */
  isAppEndpoint(url) {
    try {
      // Parse URL
      const urlObj = new URL(url, window.location.origin);
      
      // Check if same origin
      if (urlObj.origin !== window.location.origin) {
        return false;
      }
      
      // Check if it's an API endpoint
      return urlObj.pathname.startsWith('/api/');
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Convert argument to string for error message
   * @param {any} arg - Argument to stringify
   * @returns {string} String representation
   * @private
   */
  stringifyArg(arg) {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'string') return arg;
    if (arg instanceof Error) return arg.message;
    
    try {
      return JSON.stringify(arg);
    } catch (e) {
      return String(arg);
    }
  }
}

// Create singleton instance
const errorTracker = new ErrorTracker();

// Export instance
export default errorTracker;